import React, { useCallback, useRef, useState } from 'react';

import { App, Button, Card, Dropdown, Modal, Result, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { IAccessPolicyUserList } from 'interfaces/IAccessPolicies';
import accessControlService from 'v2/services/access-control';

import { Icon } from '@eduzz/ui-antd-theme';

import { useAccessPolicyUsers } from '../useAccessPolicyUsers';

type AccessUser = IAccessPolicyUserList[0];

interface IProps {
  onEdit: (user: AccessUser) => void;
}

const AccessUsers: React.FC<IProps> = ({ onEdit }) => {
  const { message } = App.useApp();
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState<AccessUser | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const { users, isFetching, isError, refetch } = useAccessPolicyUsers();

  const handleEdit = useCallback(
    (user: AccessUser) => {
      onEdit(user);
    },
    [onEdit]
  );

  const handleAskDelete = useCallback((user: AccessUser) => {
    setCurrentUser(user);
    setShowDeleteConfirmation(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
    setCurrentUser(null);
  }, []);

  const handleDelete = useCallback(async () => {
    const userToDelete = currentUserRef.current;
    setShowDeleteConfirmation(false);
    setIsDeleting(true);
    setCurrentUser(null);

    try {
      await accessControlService.deleteUser(userToDelete.id);
      message.success('Sucesso, usuário removido');
      refetch();
      setIsDeleting(false);
    } catch {
      setIsDeleting(false);
      message.error('Ops, erro na remoção do usuário');
    }
  }, [message, refetch]);

  const getMenuItems = useCallback(
    (item: AccessUser): MenuProps['items'] => [
      {
        key: 'edit',
        label: 'Editar',
        onClick: () => handleEdit(item)
      },
      {
        key: 'delete',
        label: 'Remover',
        onClick: () => handleAskDelete(item)
      }
    ],
    [handleEdit, handleAskDelete]
  );

  const columns: ColumnsType<AccessUser> = [
    {
      title: 'Usuário',
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'E-mail',
      key: 'email',
      dataIndex: 'email'
    },
    {
      title: 'Grupo',
      key: 'group_name',
      dataIndex: 'group_name'
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'right',
      render: (_, item) => (
        <Dropdown menu={{ items: getMenuItems(item) }} trigger={['click']}>
          <Button type='text' shape='circle' aria-label='Ações do usuário' icon={<Icon.Ellipsis size={16} />} />
        </Dropdown>
      )
    }
  ];

  return (
    <>
      {!!isError && (
        <Card>
          <Result status='error' title='Um erro aconteceu...' />
        </Card>
      )}

      {!isError && (
        <Card>
          <Table
            columns={columns}
            dataSource={Array.isArray(users) ? users : []}
            rowKey='id'
            loading={isFetching || isDeleting}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Nenhum registro encontrado.' }}
          />
        </Card>
      )}

      <Modal
        open={showDeleteConfirmation}
        onCancel={handleCancelDelete}
        title='Remover usuário'
        footer={
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={handleCancelDelete}>
              Cancelar
            </Button>
            <Button type='primary' danger onClick={handleDelete}>
              Remover
            </Button>
          </div>
        }
      >
        {currentUser && (
          <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
            <Typography.Text>
              Deseja realmente remover o usuário <Typography.Text strong>{currentUser.name}</Typography.Text>?
            </Typography.Text>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AccessUsers;
